import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UtensilsCrossed, ArrowLeft, Mail, Loader2, KeyRound, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot-password (email-link) state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);
  const [fpDevLink, setFpDevLink] = useState(''); // surfaced when backend has no Resend key

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleFpSend = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim() || !fpEmail.includes('@')) { setFpError('Enter a valid email address'); return; }
    setFpError('');
    setFpLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: fpEmail.trim() });
      setFpDevLink(data?.dev_link || '');
      setFpSent(true);
    } catch (e) {
      setFpError(e.response?.data?.detail || 'Failed to send reset email');
    }
    setFpLoading(false);
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setFpEmail(''); setFpError(''); setFpSent(false); setFpDevLink('');
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors">
          <ArrowLeft size={18} /><span className="text-sm font-medium">Back to Home</span>
        </Link>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 flex items-center justify-center mb-3">
              <img src="/olafel_logo.png" alt="Olafel" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Welcome back</CardTitle>
            <CardDescription className="text-stone-500">Sign in to your dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div data-testid="login-error" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@restaurant.com" required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-700">Password</Label>
                <Input data-testid="login-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="h-11" />
              </div>
              <div className="flex justify-end">
                <button type="button" data-testid="forgot-password-btn" onClick={() => setForgotOpen(true)} className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                  Forgot password?
                </button>
              </div>
              <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <p className="text-center text-sm text-stone-500 mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-orange-600 hover:text-orange-700 font-medium">Create one</Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password — email-link flow */}
      <Dialog open={forgotOpen} onOpenChange={(open) => { if (!open) closeForgot(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <KeyRound size={20} className="text-orange-600" />
              {fpSent ? 'Check your email' : 'Forgot password'}
            </DialogTitle>
            <p className="text-sm text-stone-500 pt-1">
              {fpSent
                ? "If an account exists for that email, we just sent a password reset link. The link expires in 60 minutes."
                : 'Enter the email you registered with and we\'ll email you a secure link to set a new password.'}
            </p>
          </DialogHeader>

          {fpSent ? (
            <div className="py-4 space-y-3 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="text-green-600" size={26} />
              </div>
              <p className="text-stone-700 font-medium">Email on its way to <span className="font-mono">{fpEmail}</span></p>
              {fpDevLink && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-left">
                  <p className="text-xs text-blue-600 font-semibold mb-1">DEV MODE — no email provider configured</p>
                  <a data-testid="forgot-dev-link" href={fpDevLink} className="text-xs text-blue-800 underline break-all">{fpDevLink}</a>
                </div>
              )}
              <Button onClick={closeForgot} data-testid="forgot-close-btn" className="bg-orange-600 hover:bg-orange-700 text-white">Back to Sign In</Button>
            </div>
          ) : (
            <form onSubmit={handleFpSend} className="space-y-4 py-2">
              {fpError && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{fpError}</div>}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Mail size={14} /> Email</Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={fpEmail}
                  onChange={e => setFpEmail(e.target.value)}
                  placeholder="you@restaurant.com"
                  className="h-11"
                  required
                />
              </div>
              <Button data-testid="forgot-send-link-btn" type="submit" disabled={fpLoading} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                {fpLoading ? <><Loader2 size={16} className="animate-spin mr-2" /> Sending…</> : 'Send reset link'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
